package com.example.mystybag.common.ui.views

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.ime
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.SnackbarResult
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.blur
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.zIndex
import androidx.compose.material3.Text
import com.example.mystybag.common.ui.components.custom.ImageDescriptionView
import com.example.mystybag.core.model.ImageDescription
import com.arkivanov.decompose.extensions.compose.stack.Children
import com.arkivanov.decompose.extensions.compose.stack.animation.slide
import com.arkivanov.decompose.extensions.compose.stack.animation.stackAnimation
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.core.logic.base.AppViewModel
import com.example.mystybag.core.logic.base.BaseViewModel
import com.example.mystybag.core.logic.base.BottomSheetChild
import com.example.mystybag.core.logic.base.DialogConfig
import com.example.mystybag.core.logic.helpers.AppSpecificViewModelInterface
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.helpers.WebLocaleComposition
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p96
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.views.keyboardListenerFlow
import com.example.mystybag.common.expected.AppKeyboardFocusManager
import com.example.mystybag.common.expected.SetSystemBarsColor
import com.example.mystybag.common.extensions.modifyIf
import com.example.mystybag.core.helpers.ScreenCastHelper

@Composable
fun AppView(viewModelInterface: AppSpecificViewModelInterface, appScreenFactory: @Composable (BaseViewModel) -> Unit, bottomSheetScreenFactory: @Composable (BottomSheetChild) -> Unit)  {
    val snackState          = viewModelInterface.snackState.subscribeAsState()
    val snackbarHostState   = remember { SnackbarHostState() }
    val imePadding          = WindowInsets.ime.asPaddingValues().calculateBottomPadding() + p96
    val dialogState         = viewModelInterface.dialogControl.dialogState.subscribeAsState()
    val applyBlur           = remember { mutableStateOf(false) }
    val applyIOSBlur        = AppViewModel.iosAppBlurScreen.collectAsState()
    val showCastNotAllowed  = ScreenCastHelper.castDisabledFlow.collectAsState()
    val fullScreenImage     = AppViewModel.fullScreenImageOverlay.collectAsState()

    SetSystemBarsColor()
    AppKeyboardFocusManager(keyboardListenerFlow)

    // Web: pushed Decompose screens (e.g. add/edit object) sit outside MainView's key(WebLocaleComposition.revision).
    // Reading revision here subscribes the whole app shell so Moko stringResource / sharedString refresh on language change.
    if (PlatformHelper.isWeb) {
        @Suppress("UNUSED_VARIABLE")
        val webLocaleRevision = WebLocaleComposition.revision
    }

    LaunchedEffect(dialogState.value.child?.instance) {
        applyBlur.value = dialogState.value.child?.instance is DialogConfig
    }

    Box(
        modifier = Modifier.fillMaxSize().focusable() //note .focusable() fixes issues on Samsung with Android 8, do not delete
    ) {
        BottomSheetLayoutView(
            sheetControl            = viewModelInterface.sheetControl,
            modifier                = Modifier
                .fillMaxSize()
                .modifyIf(applyIOSBlur.value) {
                    blur(radiusX = p24, radiusY = p24)
                },
            overrideChildProgress   = false,
            bottomSheetFactory      = bottomSheetScreenFactory
        ) {
            Scaffold(
                modifier            = Modifier
                    .fillMaxSize()
                    .modifyIf(applyBlur.value) { blur(radiusX = p8, radiusY = p8) },
                containerColor      = AppTheme.current.colors.background,
                contentWindowInsets = WindowInsets(p0, p0, p0, p0),
                snackbarHost        = {
                    SnackbarHost(
                        hostState = snackbarHostState,
                        modifier  = Modifier
                            .fillMaxWidth()
                            .padding(bottom = imePadding),
                        snackbar  = { data ->
                            SnackView(data)
                        }
                    )
                }
            ) {
                Children(
                    modifier  = Modifier.fillMaxSize(),
                    stack     = viewModelInterface.childStack,
                    animation = stackAnimation(slide())
                ) {
                    appScreenFactory(it.instance)
                }

                LaunchedEffect(snackState.value.child) {
                    snackState.value.child?.instance?.also { config ->
                        snackbarHostState.showSnackbar(
                            message         = config.message,
                            actionLabel     = config.action,
                            duration        = config.duration
                        ).also { result ->
                            viewModelInterface.onSnackbarClosed(result == SnackbarResult.ActionPerformed)
                        }
                    }
                }
            }

            AlertDialogView(viewModelInterface.dialogControl)
        }

        if (showCastNotAllowed.value)
            ScreenCastView(modifier = Modifier.fillMaxSize())

        // Full-screen image overlay on entire phone screen (above bottom sheet and dialog)
        fullScreenImage.value?.let { overlay ->
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .zIndex(Float.MAX_VALUE)
                    .background(AppTheme.current.colors.surface.copy(alpha = 0.98f))
                    .clickable(enabled = true, onClick = overlay.onClose),
                contentAlignment = Alignment.Center
            ) {
                ImageDescriptionView(
                    image = ImageDescription(link = overlay.url, icon = null),
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Fit
                )
                Text(
                    text = "✕",
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(16.dp)
                        .clickable(enabled = true, onClick = overlay.onClose),
                    fontSize = 28.sp,
                    color = AppTheme.current.colors.onSurface
                )
            }
        }
    }
}